import { Item, Product, Status } from "../../type";
import type { ProductBaseIdRequest } from "./elcProducts";
import { fetchLokiProxy, LokiMarket } from "./lokiProxy";

type LokiAsset = {
    location?: string | null,
    alt_text?: string | null,
    height?: number | null,
    width?: number | null,
    primary_content?: string | null,
};

type LokiProduct = {
    id: string | number,
    full_name?: string | null,
    name?: string | null,
    canonical_path?: string | null,
    short_description?: string | null,
    what_it_is?: string | null,
    description?: string | null,
    how_to_use?: string | null,
    skin_type?: Record<string, string> | null,
    time_of_use?: Record<string, string> | null,
    assets?: LokiAsset[] | null,
};

export type LokiVariant = {
    sku_id: string,
    pg_sku_base_id?: string | number | null,
    product: LokiProduct,
    rrp?: number | null,
    market_currency?: string | null,
    size?: number | null,
    size_uom?: Record<string, string> | null,
    second_size?: number | null,
    second_size_uom?: Record<string, string> | null,
    inv_status?: string | null,
    shoppability?: string | null,
    is_available_on_web?: boolean | null,
    hex_color_value?: string | null,
    shade_name?: string | null,
    assets?: LokiAsset[] | null,
};

export const fetchLokiVariants = async (filter: { variantIds: string[] }, market: LokiMarket = "US"): Promise<LokiVariant[]> =>
    await fetchLokiProxy(filter.variantIds, market) as LokiVariant[];

export const getLokiRequestSkus = (requests: ProductBaseIdRequest[]): string[] => requests.flatMap((request) => {
    if (request.variantIds?.length) return request.variantIds;
    // instoreSku is the alphanumeric Loki SKU; skuID can be a numeric legacy ID.
    const sku = request.instoreSku?.trim() || request.skuID?.trim();
    return sku ? [sku] : [];
});

const mapAssets = (assets?: LokiAsset[] | null): Item["image"] => (assets ?? [])
    .filter((asset) => Boolean(asset.location))
    .map((asset) => ({
        src: asset.location!,
        alt: asset.alt_text ?? "",
        height: asset.height ?? 0,
        width: asset.width ?? 0,
    }));

const getDayNightValue = (timeOfUse: LokiProduct["time_of_use"]): Product["dayNightValue"] => {
    const values = Object.keys(timeOfUse ?? {});
    if (values.some((value) => ["all_day", "both-time"].includes(value))) return "both";
    const day = values.some((value) => ["morning", "day", "day-time"].includes(value));
    const night = values.some((value) => ["evening", "night", "night-time"].includes(value));
    return day && night ? "both" : day ? "day" : night ? "night" : undefined;
};

const formatSize = (size?: number | null, units?: Record<string, string> | null) =>
    size == null ? "" : `${size} ${Object.values(units ?? {})[0] ?? ""}`.trim();

export const normalizeLokiProductUrl = (url?: string | null): string =>
    (url ?? "").replace(/^((?:https?:\/\/[^/?#]+)?\/?)product\//, "$1products/");

export const parseProductsFromLoki = (variants: LokiVariant[], requests: ProductBaseIdRequest[]): Product[] => {
    const byProductId = new Map<string, LokiVariant[]>();
    const bySku = new Map<string, LokiVariant>();
    variants.forEach((variant) => {
        if (!variant.product || !variant.sku_id) return;
        bySku.set(variant.sku_id, variant);
        const id = String(variant.product.id);
        const group = byProductId.get(id) ?? [];
        if (!group.some((item) => item.sku_id === variant.sku_id)) group.push(variant);
        byProductId.set(id, group);
    });

    const normalizeId = (id: string | number | null | undefined): string => id == null ? "" : String(id).trim();
    const resolveSpfVariant = (request: ProductBaseIdRequest): LokiVariant | undefined => {
        const exactSku = bySku.get(normalizeId(request.instoreSku)) || bySku.get(normalizeId(request.skuID));
        if (exactSku) return exactSku;

        const identifiers = new Set([request.productId, request.prodBaseId, request.baseId, request.skuID, request.instoreSku]
            .map(normalizeId).filter(Boolean));
        let bestGroup: LokiVariant[] | undefined;
        let bestScore = 0;
        let ambiguous = false;
        byProductId.forEach((group, productId) => {
            const matchingIds = new Set<string>();
            if (identifiers.has(productId)) matchingIds.add(productId);
            group.forEach((variant) => {
                [variant.sku_id, variant.pg_sku_base_id].map(normalizeId).forEach((id) => {
                    if (id && identifiers.has(id)) matchingIds.add(id);
                });
            });
            if (matchingIds.size > bestScore) {
                bestGroup = group;
                bestScore = matchingIds.size;
                ambiguous = false;
            } else if (matchingIds.size > 0 && matchingIds.size === bestScore) {
                ambiguous = true;
            }
        });
        if (ambiguous || !bestGroup) return undefined;
        return bestGroup.find((variant) => identifiers.has(normalizeId(variant.pg_sku_base_id))) || bestGroup[0];
    };

    const seen = new Set<string>();
    const products: Product[] = [];
    requests.forEach((request) => {
        const preserveCategory = request.category.includes("dailySpfProtectionQuiz");
        if (!preserveCategory && seen.has(request.prodBaseId)) return;
        // Prefer the exact SKU, then corroborate numeric product/SKU IDs across CMS fields.
        const recommendedVariant = preserveCategory ? resolveSpfVariant(request) : undefined;
        if (preserveCategory && !recommendedVariant) return;
        const productId = recommendedVariant ? String(recommendedVariant.product.id) : request.prodBaseId;
        const group = byProductId.get(productId);
        if (!group) return;
        seen.add(productId);
        const source = group[0].product;
        const isRecommendedVariant = (variant: LokiVariant) =>
            (recommendedVariant ? variant.sku_id === recommendedVariant.sku_id :
            variant.sku_id === request.instoreSku || variant.sku_id === request.skuID ||
            (variant.pg_sku_base_id != null && String(variant.pg_sku_base_id) === request.skuID));
        const items: Item[] = group
            // Temporary SPF display test: keep not_shoppable variants in uvProducts.
            .filter((variant) => variant.is_available_on_web !== false &&
                (preserveCategory || !variant.shoppability || variant.shoppability === "shoppable"))
            .sort((a, b) => Number(isRecommendedVariant(b)) - Number(isRecommendedVariant(a)))
            .map((variant) => {
                const images = mapAssets(variant.assets);
                const fallbackImages = mapAssets(source.assets?.filter((asset) => asset.primary_content === "productShot"));
                return {
                    sku_id: variant.sku_id,
                    price: variant.rrp == null ? "" : new Intl.NumberFormat("en-US", {
                        style: "currency", currency: variant.market_currency || "USD",
                    }).format(variant.rrp),
                    quantity: [formatSize(variant.second_size, variant.second_size_uom), formatSize(variant.size, variant.size_uom)].filter(Boolean).join("/"),
                    shadeCode: variant.hex_color_value ? `#${variant.hex_color_value.replace(/^#/, "")}` : undefined,
                    shadeName: variant.shade_name ?? undefined,
                    image: images.length ? images : fallbackImages,
                    status: variant.inv_status === "in_stock" ? Status.active : Status.oos,
                };
            });
        if (!items.length) return;

        const texts: NonNullable<Product["texts"]> = [];
        const addText = (label: string, content?: string | null) => {
            if (content) texts.push({ label, content });
        };
        addText("short_description", source.short_description);
        addText("WHO IT IS FOR", Object.values(source.skin_type ?? {}).join(", "));
        addText("WHAT_IT_IS", source.what_it_is);
        addText("WHAT_IT_DOES", source.description);
        addText("HOW_TO_USE", source.how_to_use);
        products.push({
            base_id: productId,
            prod_base_id: productId,
            skuID: recommendedVariant?.pg_sku_base_id != null
                ? String(recommendedVariant.pg_sku_base_id)
                : request.skuID || productId,
            instoreSku: request.instoreSku,
            category: request.category,
            priority: request.priority,
            name: source.full_name || source.name || "",
            sppURL: normalizeLokiProductUrl(source.canonical_path),
            price: items[0].price,
            images: [...items[0].image, ...mapAssets(source.assets)],
            tags: [],
            texts,
            items,
            dayNightValue: getDayNightValue(source.time_of_use),
        });
    });
    return products;
};

export const getLokiInfosProducts = async (requests: ProductBaseIdRequest[], market: LokiMarket = "US"): Promise<Product[]> => {
    try {
        const variants = await fetchLokiVariants({ variantIds: getLokiRequestSkus(requests) }, market);
        return parseProductsFromLoki(variants, requests);
    } catch (error) {
        console.error(`Error fetching ${market} products from Loki`, error);
        return [];
    }
};
